import { createContext, useContext, useState, useEffect } from 'react';

const GuestContext = createContext(null);

const STORAGE_KEY = 'flipvalue_guest_data';
const GUEST_SEARCH_LIMIT = 10; // 10 lifetime searches for guests
const SOFT_PROMPT_THRESHOLD = 5; // Show soft prompt at 5 searches

const getDefaultGuestData = () => ({
  searchesUsed: 0, // Lifetime search counter for guests
  hasSeenSoftPrompt: false,
  hasDismissedSoftPrompt: false,
  localHistory: [],
  localInventory: [],
  localReleases: []
});

export function GuestProvider({ children }) {
  const [guestData, setGuestData] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...getDefaultGuestData(),
          ...parsed,
          searchesUsed: parsed.searchesUsed || parsed.totalActions || 0
        };
      }
    } catch (e) {
      console.error('Error loading guest data:', e);
    }
    return getDefaultGuestData();
  });

  const [showSoftPrompt, setShowSoftPrompt] = useState(false);
  const [showLimitOverlay, setShowLimitOverlay] = useState(false);
  const [showInventoryPrompt, setShowInventoryPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Persist to localStorage whenever guestData changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(guestData));
  }, [guestData]);

  // Check if should show soft prompt
  useEffect(() => {
    if (
      guestData.searchesUsed >= SOFT_PROMPT_THRESHOLD &&
      !guestData.hasSeenSoftPrompt &&
      !guestData.hasDismissedSoftPrompt
    ) {
      setShowSoftPrompt(true);
    }
  }, [guestData.searchesUsed, guestData.hasSeenSoftPrompt, guestData.hasDismissedSoftPrompt]);

  // Update hasSeenSoftPrompt after showing prompt
  useEffect(() => {
    if (showSoftPrompt && !guestData.hasSeenSoftPrompt) {
      setGuestData(prev => ({ ...prev, hasSeenSoftPrompt: true }));
    }
  }, [showSoftPrompt, guestData.hasSeenSoftPrompt]);

  const canPerformSearch = () => {
    return guestData.searchesUsed < GUEST_SEARCH_LIMIT;
  };

  const recordSearch = () => {
    if (!canPerformSearch()) {
      setShowLimitOverlay(true);
      return false;
    }

    setGuestData(prev => ({
      ...prev,
      searchesUsed: prev.searchesUsed + 1
    }));

    return true;
  };

  const checkSearchLimit = () => {
    if (!canPerformSearch()) {
      setShowLimitOverlay(true);
      return false;
    }
    return true;
  };

  const dismissSoftPrompt = () => {
    setShowSoftPrompt(false);
    setGuestData(prev => ({ ...prev, hasDismissedSoftPrompt: true }));
  };

  const dismissLimitOverlay = () => {
    setShowLimitOverlay(false);
  };

  const promptForInventory = (action) => {
    setPendingAction(action);
    setShowInventoryPrompt(true);
  };

  const dismissInventoryPrompt = (continueAsGuest = false) => {
    setShowInventoryPrompt(false);
    if (continueAsGuest && pendingAction) {
      pendingAction();
    }
    setPendingAction(null);
  };

  const addToLocalHistory = (scan) => {
    setGuestData(prev => ({
      ...prev,
      localHistory: [scan, ...prev.localHistory].slice(0, 50)
    }));
  };

  const addToLocalInventory = (item) => {
    setGuestData(prev => ({
      ...prev,
      localInventory: [item, ...prev.localInventory]
    }));
  };

  const addToLocalReleases = (release) => {
    setGuestData(prev => ({
      ...prev,
      localReleases: [release, ...prev.localReleases]
    }));
  };

  const clearGuestData = () => {
    setGuestData(getDefaultGuestData());
  };

  const value = {
    // State
    searchesUsed: guestData.searchesUsed,
    searchesRemaining: GUEST_SEARCH_LIMIT - guestData.searchesUsed,
    guestSearchLimit: GUEST_SEARCH_LIMIT,
    localHistory: guestData.localHistory,
    localInventory: guestData.localInventory,
    localReleases: guestData.localReleases,
    
    // Legacy aliases for backward compatibility
    actionsUsed: guestData.searchesUsed,
    actionsRemaining: GUEST_SEARCH_LIMIT - guestData.searchesUsed,
    lifetimeLimit: GUEST_SEARCH_LIMIT,
    totalActions: guestData.searchesUsed,
    
    // Prompt states
    showSoftPrompt,
    showLimitOverlay,
    showInventoryPrompt,
    
    // Actions
    canPerformSearch,
    recordSearch,
    checkSearchLimit,
    // Legacy aliases
    canPerformAction: canPerformSearch,
    recordAction: recordSearch,
    checkActionLimit: checkSearchLimit,
    
    dismissSoftPrompt,
    dismissLimitOverlay,
    promptForInventory,
    dismissInventoryPrompt,
    addToLocalHistory,
    addToLocalInventory,
    addToLocalReleases,
    clearGuestData
  };

  return (
    <GuestContext.Provider value={value}>
      {children}
    </GuestContext.Provider>
  );
}

export function useGuest() {
  const context = useContext(GuestContext);
  if (!context) {
    throw new Error('useGuest must be used within a GuestProvider');
  }
  return context;
}
